const NEWSLETTER_POPUP_CLOSED_KEY = 'newsletterPopupClosed';
const NEWSLETTER_OPENER_CLOSED_KEY = 'newsletterOpenerClosed';
const NEWSLETTER_DAY_IN_MS = 24 * 60 * 60 * 1000;
const NEWSLETTER_POPUP_OPEN_EVENT = 'mymara:newsletter-popup:open-request';
const NEWSLETTER_POPUP_CLOSE_OPENER_EVENT = 'mymara:newsletter-popup:close-opener-request';
const NEWSLETTER_BLOCKING_WINDOW_SELECTOR = 'modal-dialog[open], age-verifier-popup[open], newsletter-popup[open], dialog[open], cart-drawer.active, #product-modal.active, .drawer.active';

class NewsletterPopup extends ModalDialog {
  constructor() {
    super();
    this.reopenDays = parseInt(this.dataset.reopen, 10) || 1;
  }

  hide() {
    super.hide();
    const expirationDate = Date.now() + this.reopenDays * NEWSLETTER_DAY_IN_MS;
    localStorage.setItem(NEWSLETTER_POPUP_CLOSED_KEY, expirationDate);
  }
}

if (!customElements.get('newsletter-popup')) {
  customElements.define('newsletter-popup', NewsletterPopup);
}

function getNewsletterExpiration(key) {
  const value = parseInt(localStorage.getItem(key), 10);
  return Number.isFinite(value) ? value : 0;
}

function shouldKeepNewsletterClosed(key) {
  return Date.now() <= getNewsletterExpiration(key);
}

function isAnotherWindowOpen() {
  return document.body.classList.contains('overflow-hidden')
    || document.body.classList.contains('quickview-open')
    || Boolean(document.querySelector(NEWSLETTER_BLOCKING_WINDOW_SELECTOR));
}

function hideNewsletterOpener(opener) {
  if (opener) {
    opener.style.display = 'none';
  }
}

function getNewsletterInlineMarkup(config) {
  const { inlinePayloadId } = config.dataset;
  if (!inlinePayloadId) return '';

  const inlinePayload = document.getElementById(inlinePayloadId);
  if (!inlinePayload) return '';

  try {
    return JSON.parse(inlinePayload.textContent || '""');
  } catch (error) {
    console.error('Error parsing newsletter popup inline payload:', error);
    return '';
  }
}

function extractNewsletterMarkup(markupResponse, modalId) {
  const parsedDocument = new DOMParser().parseFromString(markupResponse, 'text/html');
  const modal = parsedDocument.getElementById(modalId) || parsedDocument.querySelector('newsletter-popup');

  return modal ? modal.outerHTML : '';
}

function loadNewsletterMarkup(config) {
  if (config._newsletterMarkupPromise) return config._newsletterMarkupPromise;

  const inlineMarkup = getNewsletterInlineMarkup(config);
  if (inlineMarkup) {
    config._newsletterMarkupPromise = Promise.resolve(inlineMarkup);
    return config._newsletterMarkupPromise;
  }

  if (!config.dataset.fetchUrl) {
    config._newsletterMarkupPromise = Promise.resolve('');
    return config._newsletterMarkupPromise;
  }

  config._newsletterMarkupPromise = fetch(config.dataset.fetchUrl, { credentials: 'same-origin' })
    .then((response) => response.text())
    .then((responseText) => extractNewsletterMarkup(responseText, config.dataset.modalId))
    .catch((error) => {
      config._newsletterMarkupPromise = null;
      console.error('Error loading newsletter popup markup:', error);
      return '';
    });

  return config._newsletterMarkupPromise;
}

async function ensureNewsletterPopup(config) {
  const { modalId } = config.dataset;
  let modal = document.getElementById(modalId);

  if (modal) return modal;

  const markup = await loadNewsletterMarkup(config);
  if (!markup) return null;

  config.insertAdjacentHTML('afterend', markup);
  modal = document.getElementById(modalId);

  return modal;
}

function buildNewsletterPopupController(config) {
  const opener = document.querySelector(`[data-newsletter-template-id="${config.id}"]`);
  const openerButton = opener?.querySelector('button');
  const openerCloseButton = opener?.querySelector('.newsletter-modal__close');
  const reopenDays = parseInt(config.dataset.reopen, 10) || 1;
  const openDelay = parseInt(config.dataset.delay, 10) * 1000 || 2000;
  const sectionId = config.dataset.sectionId;

  const showPopup = async (trigger = openerButton, automatic = false, retryDelay = openDelay / 2) => {
    if (!config.isConnected) return null;

    const existingModal = document.getElementById(config.dataset.modalId);
    if (existingModal?.hasAttribute('open')) return existingModal;

    if (automatic) {
      if (shouldKeepNewsletterClosed(NEWSLETTER_POPUP_CLOSED_KEY)) return null;
      if (isAnotherWindowOpen()) {
        window.setTimeout(() => showPopup(trigger, true, retryDelay), retryDelay);
        return null;
      }
    }

    const modal = await ensureNewsletterPopup(config);
    if (!config.isConnected || !modal || modal.hasAttribute('open')) return modal;

    if (automatic) {
      if (shouldKeepNewsletterClosed(NEWSLETTER_POPUP_CLOSED_KEY)) return null;
      if (isAnotherWindowOpen()) {
        window.setTimeout(() => showPopup(trigger, true, retryDelay), retryDelay);
        return modal;
      }
    }

    modal.show(trigger);
    return modal;
  };

  if (shouldKeepNewsletterClosed(NEWSLETTER_OPENER_CLOSED_KEY)) {
    hideNewsletterOpener(opener);
  }

  openerButton?.addEventListener('click', () => {
    showPopup(openerButton);
  });

  openerCloseButton?.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    hideNewsletterOpener(opener);

    const expirationDate = Date.now() + reopenDays * NEWSLETTER_DAY_IN_MS;
    localStorage.setItem(NEWSLETTER_OPENER_CLOSED_KEY, expirationDate);
  });

  if (config.dataset.openInEditor === 'true') {
    showPopup(openerButton);
  }

  if (Shopify.designMode) {
    document.addEventListener('shopify:section:select', (event) => {
      if (event.detail.sectionId === sectionId) {
        showPopup(openerButton);
      }
    });

    document.addEventListener('shopify:section:deselect', (event) => {
      if (event.detail.sectionId !== sectionId) return;

      const modal = document.getElementById(config.dataset.modalId);
      if (modal?.hasAttribute('open')) {
        modal.hide();
      }
    });
  }

  return {
    sectionId,
    show: showPopup,
    hideOpener() {
      hideNewsletterOpener(opener);
      const expirationDate = Date.now() + reopenDays * NEWSLETTER_DAY_IN_MS;
      localStorage.setItem(NEWSLETTER_OPENER_CLOSED_KEY, expirationDate);
    },
  };
}

const newsletterPopupControllers = new Map();

function initNewsletterPopupConfigs(root = document) {
  root.querySelectorAll('[data-newsletter-popup-config]').forEach((config) => {
    if (config.dataset.newsletterPopupInitialized === 'true') return;

    config.dataset.newsletterPopupInitialized = 'true';
    newsletterPopupControllers.set(config.id, buildNewsletterPopupController(config));
  });
}

function getNewsletterPopupController(configId) {
  if (!configId) return null;
  return newsletterPopupControllers.get(configId) || null;
}

document.addEventListener(NEWSLETTER_POPUP_OPEN_EVENT, (event) => {
  const controller = getNewsletterPopupController(event.detail?.configId);
  controller?.show(undefined, event.detail?.automatic === true);
});

document.addEventListener(NEWSLETTER_POPUP_CLOSE_OPENER_EVENT, (event) => {
  const controller = getNewsletterPopupController(event.detail?.configId);
  controller?.hideOpener();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initNewsletterPopupConfigs(), { once: true });
} else {
  initNewsletterPopupConfigs();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) {
    initNewsletterPopupConfigs(event.target);
  }
});
